/** @format */
import { castArray } from 'lodash';
import { stringify } from 'qs';

import { getConfig } from '@toucan/common/services/nextjs/settings';

import { convertKeyNames, toSnakeCase } from '@toucan/client/global/axios';
import {
    MarketCode,
    marketsWithFaultyItems,
} from '@toucan/client/global/market';

import { ProductConditionStarRating } from './constants';
import { escapeSolrQuery, transformToSearchValues } from './helpers';
import { FilterQuery, Query } from './types';

function prepareFilterQuery(
    allowFaultyItems: boolean,
    filterQuery: FilterQuery = {}
): FilterQuery {
    const query: FilterQuery = Object.entries(filterQuery).reduce(
        (accum, [key, value]) => ({
            ...accum,
            [key]: transformToSearchValues(key, value),
        }),
        {}
    );

    if (allowFaultyItems) {
        return query;
    }

    // If we are filtering on multiple conditions, we need to join and
    // wrap the values in parentheses otherwise we can just print the
    // filter value as is.
    function formatConditionQuery(
        starRatings: (string | number | boolean)[] = []
    ): string {
        const query = starRatings.join(' OR ');
        return starRatings.length > 1 ? `(${query})` : query;
    }

    // If the filter is set then we need to suffix an AND on the end of it,
    // as we add the NOT to the end in the filter query to stop the faulty
    // products from being in search results
    const nextProductConditionStarRating = query?.productConditionStarRating
        ? `${formatConditionQuery(
              castArray(query.productConditionStarRating)
          )} AND`
        : '';

    // If it is not a primary market then we want to remove Faulty products from search results
    return {
        ...query,
        productConditionStarRating: `${nextProductConditionStarRating} NOT ${ProductConditionStarRating.ZERO}`,
    };
}

export function getSearchQueryParams(search: Query, marketCode: MarketCode) {
    const { publicRuntimeConfig } = getConfig();

    const allowFaultyItems = marketCode
        ? marketsWithFaultyItems.includes(marketCode)
        : false;

    const queryParams = stringify(
        convertKeyNames(
            {
                ...search,
                query: escapeSolrQuery(search?.query),
                filterQuery: prepareFilterQuery(
                    allowFaultyItems,
                    search.filterQuery
                ),
                minimumMatch: publicRuntimeConfig.minimumMatchForSearch,
            },
            toSnakeCase
        ),
        {
            encodeValuesOnly: true,
            arrayFormat: 'repeat',
        }
    );

    return queryParams;
}
